// row 111 SelectionBar: the bar the slate composer starts from (DC-100), pinned to the board's foot (layer 5): a card
// surface with --shadow, the chosen candidates' initials stacked, their count, *Submit* (the one primary, blocked with
// its reason until one is chosen) and *Clear*. It is a toolbar: one tab stop in, the arrow keys move between its
// actions. `preview` (a gallery section) draws it in place instead of pinned.

import { useId } from "react";
import { Button, type Blocked } from "../core/Button";
import { Count } from "../core/Count";
import { CORE_LABELS } from "../core/labels";
import { Avatars } from "../core/Person";
import { ACTION_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { useRoving } from "../lib/useRoving";
import { SELBAR_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export interface SelectionBarItem {
  name: string;
  /** The hue key (the Candidate ID). */
  hueKey?: string | undefined;
  tint?: `var(--${string})` | undefined;
}

export interface SelectionBarProps {
  /** The chosen candidates, in the order chosen. */
  selected: readonly SelectionBarItem[];
  onSubmit?: (() => void) | undefined;
  onClear?: (() => void) | undefined;
  /** Names the bar (default *Candidate slate submission*). */
  label?: string | undefined;
  /** The primary's words (default *Submit*). */
  submitLabel?: string | undefined;
  /** Why Submit waits when nothing is chosen (default the DS row's line, until his wording). */
  emptyReason?: string | undefined;
  /** Another rule that blocks Submit even with a selection. */
  blocked?: Blocked | undefined;
  busy?: boolean | undefined;
  /** At most this many initials are drawn, the rest a +n count (default 5). */
  max?: number | undefined;
  preview?: boolean | undefined;
  can?: boolean | undefined;
  hs?: { submit?: Hs | undefined; clear?: Hs | undefined } | undefined;
  className?: string | undefined;
}

export function SelectionBar({
  selected, onSubmit, onClear, label = SELBAR_LABELS.candidateSlateSubmission, submitLabel = ACTION_LABELS.submit,
  emptyReason = SELBAR_LABELS.submitUntilOneSelected, blocked, busy, max = 5, preview, can, hs, className,
}: SelectionBarProps) {
  const id = useId();
  const roving = useRoving({ count: 2, orientation: "horizontal", wrap: true });
  if (can === false) return null;
  const none = selected.length === 0;
  const block = blocked ?? (none ? { reason: emptyReason } : undefined);
  return (
    <div
      className={cx("mc-selbar", preview && "mc-selbar-pv", className)}
      role="toolbar"
      aria-labelledby={`${id}-l`}
      onKeyDown={roving.container.onKeyDown}
    >
      <span className="mc-selbar-t" id={`${id}-l`}>{label}</span>
      {none ? null : (
        <Avatars items={selected.map((item) => ({ name: item.name, hueKey: item.hueKey, candidate: true, tint: item.tint }))} size={32} max={max} />
      )}
      <Count n={selected.length} className="mc-selbar-n" />
      <span className="mc-selbar-a">
        <Button tone="ghost" {...roving.item(0)} onClick={onClear} hs={hs?.clear}>{CORE_LABELS.clear}</Button>
        <Button tone="primary" {...roving.item(1)} blocked={block} busy={busy} onClick={onSubmit} hs={hs?.submit}>{submitLabel}</Button>
      </span>
    </div>
  );
}
